// src/App.js
import React, { useEffect, useState } from 'react';
import { Amplify } from 'aws-amplify';
import awsExports from './aws-exports';
import { ThemeProvider, Loader } from '@aws-amplify/ui-react';
import { FaceLivenessDetector } from '@aws-amplify/ui-react-liveness';
import '@aws-amplify/ui-react/styles.css';

Amplify.configure(awsExports);

const styles = {
  container: {
    maxWidth: 420,
    margin: '2rem auto',
    padding: '2rem',
    fontFamily: 'Arial, sans-serif',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    border: '1px solid #d1d5db',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
  },
  header: {
    fontSize: '1.75rem',
    marginBottom: '1.5rem',
    color: '#1e3a8a',
    textAlign: 'center',
  },
  stepCard: {
    marginBottom: '1.5rem',
    padding: '1.25rem',
    backgroundColor: '#f9fafb',
    borderRadius: '6px',
    border: '1px solid #e5e7eb',
  },
  icon: {
    fontSize: '1.5rem',
    verticalAlign: 'middle',
    marginRight: '0.5rem',
  },
  stepHeader: {
    display: 'inline-block',
    fontSize: '1.25rem',
    verticalAlign: 'middle',
    margin: 0,
    color: '#1e3a8a',
  },
  button: {
    padding: '0.75rem',
    fontSize: '1rem',
    cursor: 'pointer',
    borderRadius: '4px',
    border: 'none',
    backgroundColor: '#1e40af',
    color: '#ffffff',
    marginTop: '0.75rem',
    width: '100%',
  },
  disabledButton: {
    backgroundColor: '#9ca3af',
    cursor: 'not-allowed',
  },
  card: {
    marginTop: '1rem',
    padding: '1rem',
    border: '2px dashed #cbd5e1',
    borderRadius: '8px',
    backgroundColor: '#ffffff',
  },
  input: {
    display: 'block',
    margin: '1rem auto',
  },
  finalMessage: {
    textAlign: 'center',
    fontSize: '1.25rem',
    color: '#1e3a8a',
    marginTop: '2rem',
  },
};

function App() {
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [showDetector, setShowDetector] = useState(false);

  const [idFile, setIdFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [idResult, setIdResult] = useState(null);
  const [legalName, setlegalName] = useState("First Last");

  // Initialize liveness session
  useEffect(() => {
    fetch('/api/create', { method: 'POST' })
      .then(res => res.json())
      .then(data => setSessionId(data.sessionId))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    //document.title = `ID Verification`;
    const params = new URLSearchParams(window.location.search);
    const session = params.get('session');
    fetch('/api/session?session='+encodeURIComponent(session)+"&setter="+sessionId, { method: 'GET' })
      .then(res => res.text())
      .then(data => {
        if(data === "false") {
          setlegalName(false)
        } else {
          setlegalName(data)
        }
      })
  }, [sessionId]);

  const isSkip     = idResult?.type === 'skip';

  useEffect(() => {
    if (isSkip && sessionId) {
      async function submit() {
        await fetch(`/api/submit?setter=${sessionId}`, {
          method: 'GET'
        }).catch(console.error)

        window.parent.postMessage("verifyDone", '*')
      }
      submit()
    }
  }, [isSkip, sessionId]);

  // Handle liveness analysis complete
  const handleAnalysisComplete = async () => {
    const res = await fetch(
      `/api/results?sessionId=${sessionId}`
    );
    const data = await res.json();
    setResult(data);
    setShowDetector(false);
  };

  // Start or restart liveness check
  const startLiveness = () => {
    setResult(null);
    setShowDetector(true);
    setIdFile(null);
    setIdResult(null);
  };

  // ID file selection
  const handleIdFileChange = e => {
    if (e.target.files.length) {
      setIdFile(e.target.files[0]);
      setIdResult(null);
    }
  };

  // Upload ID image
  const handleIdUpload = async () => {
    if (!idFile) return;
    setUploading(true);
    const form = new FormData();
    form.append('idImage', idFile);
    try {
      const res = await fetch(
        '/api/uploadID?setter='+sessionId,
        { method: 'POST', body: form }
      );
      const data = await res.json();
      setIdResult(data);
    } catch (err) {
      console.error(err);
      setIdResult({ type: 'face' });
    } finally {
      setUploading(false);
    }
  };

  // Continue despite text-only ID
  const handleContinue = () => {
    setIdResult({ type: 'skip' });
  };

  if (loading) return <Loader />;

  // Step 1 flags
  const step1Done = result?.isLive === true;
  const step1Failed = result !== null && result.isLive === false;

  // ID flow flags based purely on type
  const isFaceCase = idResult?.type === 'face';
  const isTextCase = idResult?.type === 'text';

  // Invalid session
  if (legalName === false) {
    return (
      <div style={styles.container}>
        <h1 style={styles.header}>Invalid Session</h1>
        <p style={styles.finalMessage}>
          Please reopen the ID verification from the panel.
        </p>
      </div>
    );
  }

  // Final completed screen after skip
  if (isSkip) {
    return (
      <div style={styles.container}>
        <h1 style={styles.header}>ID Verification Complete</h1>
        <p style={styles.finalMessage}>
          Please wait 24 hours for it to be reviewed.
        </p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h1 style={styles.header}>ID Verification For {legalName}</h1>

      {/* Step 1 */}
      <div style={styles.stepCard}>
        <span style={styles.icon}>{step1Done ? '✅' : '🔒'}</span>
        <h2 style={styles.stepHeader}>Take a picture of your face.</h2>

        {step1Failed ? (
          <div style={{ marginTop: '1rem', color: 'red' }}>
            <p>Step 1 failed</p>
            <button style={styles.button} onClick={() => window.location.reload()}>
              Restart
            </button>
          </div>
        ) : step1Done ? (
          <p style={{ marginTop: '1rem', color: 'green' }}>Step 1 completed</p>
        ) : showDetector ? (
          <ThemeProvider>
            <FaceLivenessDetector
              sessionId={sessionId}
              region={process.env.REACT_APP_AWS_REGION}
              onAnalysisComplete={handleAnalysisComplete}
              onError={() => {
                setShowDetector(false);
                setResult({ isLive: false });
              }}
            />
          </ThemeProvider>
        ) : (
          <button style={styles.button} onClick={startLiveness}>
            Start
          </button>
        )}
      </div>

      {/* Step 2 */}
      <div style={styles.stepCard}>
        <span style={styles.icon}>{isSkip ? '✅' : '🔒'}</span>
        <h2 style={styles.stepHeader}>Upload a government ID.</h2>

        {/* face-case: always restart */}
        {isFaceCase && (
          <div style={{ marginTop: '1rem', color: 'red' }}>
            <p>Step 2 failed</p>
            <button style={styles.button} onClick={() => window.location.reload()}>
              Restart
            </button>
          </div>
        )}

        {/* text-case: allow continue or restart */}
        {isTextCase && (
          <div style={{ marginTop: '1rem', color: '#9a6710' }}>
            <p>
              ID not validated. You can still continue but review might take longer. It is better to restart.
            </p>
            <button style={styles.button} onClick={handleContinue}>
              Continue
            </button>
            <button
              style={{ ...styles.button, marginTop: '0.5rem' }}
              onClick={() => window.location.reload()}
            >
              Restart
            </button>
          </div>
        )}

        {/* locked until step 1 */}
        {!step1Done && !isFaceCase && !isTextCase && (
          <p style={{ marginTop: '0.5rem', color: '#6b7280' }}>
            Complete Step 1 to unlock
          </p>
        )}

        {/* upload UI */}
        {step1Done && !isFaceCase && !isTextCase && (
          <div style={styles.card}>
            <input
              type="file"
              accept="image/*"
              onChange={handleIdFileChange}
              style={styles.input}
              disabled={uploading}
            />
            <button
              style={{
                ...styles.button,
                ...(uploading || !idFile ? styles.disabledButton : {}),
              }}
              onClick={handleIdUpload}
              disabled={uploading || !idFile}
            >
              {uploading ? 'Uploading...' : 'Upload ID'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;